/**
 * vehicleEquipmentHelper.ts
 *
 * Helper pour la gestion de l'équipement véhicule:
 * - Application des presets profil complet
 * - Synchronisation sellette <-> axle_configuration
 * - Normalisation des données
 * - Validation des configurations
 */

import type { Vehicle } from '../services/vehicle.service'
import type { VehicleEquipmentPreset } from './vehicleEquipmentPresets'
import type { VehicleAxleConfiguration } from '../types/vehiclePhysics'
import { calculateTargetCG, getAllPresets, resolvePresetMass, presetInteriorVolume } from './vehicleEquipmentPresets'

// ─── Application des presets ───────────────────────────────────────────────────

/**
 * Applique un preset profil véhicule complet à un véhicule
 * @param vehicle - Véhicule actuel (peut être partiel)
 * @param preset - Preset à appliquer
 * @returns Véhicule mis à jour avec le preset
 */
export function applyVehicleEquipmentPreset(
  vehicle: Partial<Vehicle>,
  preset: VehicleEquipmentPreset
): Partial<Vehicle> {
  const targetCG = preset.targetCG || calculateTargetCG(preset)
  const mass = resolvePresetMass(preset)

  // Déterminer si ce type de véhicule admet une sellette
  // Seuls les tracteurs et semi-remorques ont une sellette
  const hasFifthWheel = preset.category === 'tracteur' || preset.category === 'semi' ||
    preset.vehicleType === 'SEMI_TRAILER' || preset.vehicleType === 'TRAILER'

  const updated: Partial<Vehicle> = {
    ...vehicle,
    type: preset.vehicleType,
    // Dimensions (mm)
    length: preset.dimensions?.length,
    width: preset.dimensions?.width,
    height: preset.dimensions?.height,
    // Capacités
    capacity_volume: presetInteriorVolume(preset),
    capacity_weight: mass.payloadWeight,
    empty_weight: preset.emptyWeight,
    max_weight_permitted: mass.individualGrossWeight,
    max_combination_weight: mass.combinationGrossWeight,
    mass_contract_version: 'vehicle_mass_contract.v1',
    mass_provenance: {
      contract_version: 'vehicle_mass_contract.v1',
      source: mass.source,
      preset_id: preset.id,
      preset_version: mass.version,
    },
    // Configuration essieux
    axle_configuration: {
      ...preset.axleConfiguration,
      // S'assurer que les champs de synchronisation backend sont présents
      kingPinPosition: preset.axleConfiguration.kingPinPosition,
      fifthWheelMaxLoad: preset.axleConfiguration.fifthWheelMaxLoad,
    },
    // Sellette (si applicable)
    fifth_wheel: hasFifthWheel ? preset.fifthWheel : null,
    // Contraintes d'optimisation
    max_floor_pressure: preset.constraints?.maxFloorPressure,
    pressure_cell_target_size: preset.constraints?.pressureCellTargetSize,
    target_cg_x: Math.round(targetCG.x * 100) / 100,
    target_cg_y: Math.round(targetCG.y * 100) / 100,
    max_cg_deviation: 0.2, // Valeur par défaut
  }

  // Pour les véhicules sans sellette, nettoyer les champs de sellette dans axle_configuration
  if (!hasFifthWheel && updated.axle_configuration) {
    const { kingPinPosition, fifthWheelMaxLoad, ...axleConfigWithoutFifthWheel } = updated.axle_configuration as any
    updated.axle_configuration = axleConfigWithoutFifthWheel
  }

  return updated
}

/**
 * Applique un preset à un contexte d'optimisation (OptimizationPage)
 * @param formData - Données du formulaire d'optimisation
 * @param preset - Preset à appliquer
 * @returns Données mises à jour
 */
export function applyPresetToOptimizationContext(
  formData: any,
  preset: VehicleEquipmentPreset
): any {
  const targetCG = preset.targetCG || calculateTargetCG(preset)
  const mass = resolvePresetMass(preset)

  // Déterminer si ce type de véhicule admet une sellette
  // Seuls les tracteurs et semi-remorques ont une sellette
  const hasFifthWheel = preset.category === 'tracteur' || preset.category === 'semi' ||
    preset.vehicleType === 'SEMI_TRAILER' || preset.vehicleType === 'TRAILER'

  const vehicle = {
    ...formData.vehicle,
    type: preset.vehicleType,
    empty_weight: preset.emptyWeight ?? formData.vehicle?.empty_weight ?? null,
    max_weight_permitted: mass.individualGrossWeight,
    max_combination_weight: mass.combinationGrossWeight,
    capacity_weight: mass.payloadWeight,
    max_weight: mass.payloadWeight,
    capacity_volume: presetInteriorVolume(preset),
    mass_contract_version: 'vehicle_mass_contract.v1',
    mass_provenance: {
      contract_version: 'vehicle_mass_contract.v1',
      source: mass.source,
      preset_id: preset.id,
      preset_version: mass.version,
    },
    dimensions: {
      length: preset.dimensions?.length || formData.vehicle?.dimensions?.length || 10000,
      width: preset.dimensions?.width || formData.vehicle?.dimensions?.width || 2500,
      height: preset.dimensions?.height || formData.vehicle?.dimensions?.height || 2500,
    },
    axle_configuration: {
      ...preset.axleConfiguration,
      kingPinPosition: preset.axleConfiguration.kingPinPosition,
      fifthWheelMaxLoad: preset.axleConfiguration.fifthWheelMaxLoad,
    },
    fifth_wheel: hasFifthWheel && preset.fifthWheel ? {
      ...preset.fifthWheel,
      maxLoad: preset.axleConfiguration.fifthWheelMaxLoad || preset.fifthWheel.maxLoad,
    } : undefined,
  }

  // Pour les véhicules sans sellette, nettoyer les champs de sellette dans axle_configuration
  if (!hasFifthWheel && vehicle.axle_configuration) {
    const { kingPinPosition, fifthWheelMaxLoad, ...axleConfigWithoutFifthWheel } = vehicle.axle_configuration
    vehicle.axle_configuration = axleConfigWithoutFifthWheel
  }

  return {
    ...formData,
    vehicle,
    loadSpace: {
      ...formData.loadSpace,
      length: preset.dimensions?.length || formData.loadSpace?.length || 10000,
      width: preset.dimensions?.width || formData.loadSpace?.width || 2500,
      height: preset.dimensions?.height || formData.loadSpace?.height || 2500,
      maxWeight: mass.payloadWeight ?? formData.loadSpace?.maxWeight ?? null,
      emptyWeight: preset.emptyWeight ?? formData.loadSpace?.emptyWeight ?? null,
      maxTotalWeight: mass.individualGrossWeight ?? formData.loadSpace?.maxTotalWeight ?? null,
    },
    constraints: {
      ...formData.constraints,
      maxWeight: mass.payloadWeight,
      maxStackHeight: preset.constraints?.maxStackHeight,
      maxFloorPressure: preset.constraints?.maxFloorPressure,
      pressureCellTargetSize: preset.constraints?.pressureCellTargetSize,
    },
    constraintEngineConfig: {
      ...formData.constraintEngineConfig,
      targetCG: {
        x: Math.round(targetCG.x * 100) / 100,
        y: Math.round(targetCG.y * 100) / 100,
      },
    },
  }
}

/**
 * Applique un preset à un véhicule pour le composant 3D (VehicleConfigRail)
 * Attends un véhicule avec dimensions en mm
 * @param vehicle - Véhicule actuel (avec dimensions en mm)
 * @param preset - Preset à appliquer
 * @returns Véhicule mis à jour avec le preset
 */
export function applyPresetToVehicleVisualizer(
  vehicle: any,
  preset: VehicleEquipmentPreset
): any {
  const targetCG = preset.targetCG || calculateTargetCG(preset)
  const mass = resolvePresetMass(preset)

  // Pour les conteneurs, ne pas appliquer la configuration d'essieux
  const shouldApplyAxleConfig = preset.vehicleType !== 'CONTAINER' &&
    preset.axleConfiguration?.axles &&
    preset.axleConfiguration.axles.length > 0

  // Déterminer si ce type de véhicule admet une sellette
  // Seuls les tracteurs et semi-remorques ont une sellette
  const hasFifthWheel = preset.category === 'tracteur' || preset.category === 'semi' ||
    preset.vehicleType === 'SEMI_TRAILER' || preset.vehicleType === 'TRAILER'

  const baseVehicle = {
    ...vehicle,
    type: preset.vehicleType,
    dimensions: {
      length: preset.dimensions?.length || vehicle.dimensions?.length || 12000,
      width: preset.dimensions?.width || vehicle.dimensions?.width || 2500,
      height: preset.dimensions?.height || vehicle.dimensions?.height || 2600,
    },
    capacity_volume: presetInteriorVolume(preset),
    capacity_weight: mass.payloadWeight,
    empty_weight: preset.emptyWeight ?? vehicle.empty_weight,
    max_weight_permitted: mass.individualGrossWeight,
    max_combination_weight: mass.combinationGrossWeight,
    mass_contract_version: 'vehicle_mass_contract.v1',
    mass_provenance: {
      contract_version: 'vehicle_mass_contract.v1',
      source: mass.source,
      preset_id: preset.id,
      preset_version: mass.version,
    },
    max_floor_pressure: preset.constraints?.maxFloorPressure ?? vehicle.max_floor_pressure,
    pressure_cell_target_size: preset.constraints?.pressureCellTargetSize ?? vehicle.pressure_cell_target_size,
    max_cg_deviation: 0.2, // Valeur par défaut
    constraints: preset.constraints ? {
      maxWeight: mass.payloadWeight,
      maxStackHeight: preset.constraints.maxStackHeight,
      maxFloorPressure: preset.constraints.maxFloorPressure,
      pressureCellTargetSize: preset.constraints.pressureCellTargetSize,
    } : vehicle.constraints,
    target_cg_x: Math.round((targetCG.x ?? vehicle.target_cg_x) * 100) / 100,
    target_cg_y: Math.round((targetCG.y ?? vehicle.target_cg_y) * 100) / 100,
    constraintEngineConfig: {
      ...vehicle.constraintEngineConfig,
      targetCG: targetCG || vehicle.constraintEngineConfig?.targetCG,
    },
  }

  // Gérer la configuration d'essieux et de sellette
  if (shouldApplyAxleConfig) {
    baseVehicle.axle_configuration = {
      ...preset.axleConfiguration,
      kingPinPosition: preset.axleConfiguration.kingPinPosition,
      fifthWheelMaxLoad: preset.axleConfiguration.fifthWheelMaxLoad,
    }
  } else {
    baseVehicle.axle_configuration = null
  }

  // Gérer la sellette : seulement pour tracteurs et semi-remorques
  if (hasFifthWheel && preset.fifthWheel) {
    baseVehicle.fifth_wheel = {
      ...preset.fifthWheel,
      maxLoad: preset.axleConfiguration.fifthWheelMaxLoad || preset.fifthWheel.maxLoad,
    }
  } else {
    // Pour les véhicules sans sellette (porteurs, vans, conteneurs), forcer à null
    baseVehicle.fifth_wheel = null
    // Nettoyer aussi les champs de sellette dans axle_configuration
    if (baseVehicle.axle_configuration) {
      const { kingPinPosition, fifthWheelMaxLoad, ...axleConfigWithoutFifthWheel } = baseVehicle.axle_configuration
      baseVehicle.axle_configuration = axleConfigWithoutFifthWheel
    }
  }

  return baseVehicle
}

// ─── Synchronisation Sellette <-> Axle Configuration ─────────────────────────

/**
 * Synchronise les données de la sellette vers axle_configuration
 * Le backend lit kingPinPosition et fifthWheelMaxLoad depuis axle_configuration
 * @param vehicle - Véhicule à synchroniser
 * @returns Véhicule synchronisé
 */
export function syncFifthWheelToAxleConfig(vehicle: Partial<Vehicle>): Partial<Vehicle> {
  if (!vehicle.fifth_wheel) return vehicle

  const axleConfig = typeof vehicle.axle_configuration === 'object'
    ? { ...vehicle.axle_configuration as VehicleAxleConfiguration }
    : {}

  return {
    ...vehicle,
    axle_configuration: {
      ...axleConfig,
      kingPinPosition: vehicle.fifth_wheel.position,
      fifthWheelMaxLoad: vehicle.fifth_wheel.maxLoad,
    } as any,
  }
}

/**
 * Synchronise les données d'axle_configuration vers la sellette
 * Utile quand on charge un véhicule depuis le backend
 * @param vehicle - Véhicule à synchroniser
 * @returns Véhicule synchronisé
 */
export function syncAxleConfigToFifthWheel(vehicle: Partial<Vehicle>): Partial<Vehicle> {
  const axleConfig = typeof vehicle.axle_configuration === 'object'
    ? (vehicle.axle_configuration as VehicleAxleConfiguration)
    : null

  if (!axleConfig?.kingPinPosition && !axleConfig?.fifthWheelMaxLoad) {
    return vehicle
  }

  return {
    ...vehicle,
    fifth_wheel: {
      position: axleConfig.kingPinPosition || vehicle.fifth_wheel?.position || 0,
      type: vehicle.fifth_wheel?.type || 'standard',
      maxLoad: axleConfig.fifthWheelMaxLoad || vehicle.fifth_wheel?.maxLoad || 0,
    },
  }
}

// ─── Normalisation ───────────────────────────────────────────────────────────

/**
 * Normalise une configuration d'essieux
 * @param config - Configuration brute
 * @returns Configuration normalisée
 */
export function normalizeAxleConfiguration(config: any): VehicleAxleConfiguration | null {
  if (!config) return null

  // Si c'est une string JSON, la parser
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config)
    } catch (e) {
      return null
    }
  }

  // Si ce n'est pas un objet, retourner null
  if (typeof config !== 'object') return null

  // Normaliser les groupes d'essieux: s'assurer que axleIds est présent
  const axleGroups = config.axleGroups?.map((g: any) => ({
    ...g,
    axleIds: g.axleIds || g.axles || [],
  })) || []

  return {
    physicsType: config.physicsType || 'custom',
    trackWidth: config.trackWidth || 2500,
    kingPinPosition: config.kingPinPosition,
    fifthWheelMaxLoad: config.fifthWheelMaxLoad,
    enforceAxleLimits: config.enforceAxleLimits !== false,
    useDynamicStability: config.useDynamicStability || false,
    axles: config.axles || [],
    axleGroups,
    region: config.region || 'EU',
  }
}

/**
 * Normalise les données d'équipement véhicule
 * @param vehicle - Véhicule à normaliser
 * @returns Véhicule normalisé
 */
export function normalizeVehicleEquipment(vehicle: Partial<Vehicle>): Partial<Vehicle> {
  return {
    ...vehicle,
    axle_configuration: normalizeAxleConfiguration(vehicle.axle_configuration),
    fifth_wheel: syncAxleConfigToFifthWheel(vehicle).fifth_wheel,
  }
}

// ─── Validation ───────────────────────────────────────────────────────────────

export interface ValidationError {
  field: string
  message: string
  severity: 'error' | 'warning'
}

/**
 * Valide une configuration d'essieux
 * @param config - Configuration à valider
 * @returns Liste des erreurs/warnings
 */
export function validateAxleConfiguration(config: VehicleAxleConfiguration | null): ValidationError[] {
  const errors: ValidationError[] = []

  if (!config) {
    return [{ field: 'axle_configuration', message: 'Configuration essieux requise', severity: 'error' }]
  }

  // Vérifier physicsType
  if (!config.physicsType) {
    errors.push({ field: 'physicsType', message: 'Type physique requis', severity: 'error' })
  }

  // Vérifier kingPinPosition pour semi-remorque
  if (config.physicsType === 'tractor_semitrailer' && !config.kingPinPosition) {
    errors.push({
      field: 'kingPinPosition',
      message: 'Position sellette obligatoire pour semi-remorque',
      severity: 'error'
    })
  }

  // Vérifier les essieux
  if (!config.axles || config.axles.length === 0) {
    errors.push({ field: 'axles', message: 'Au moins un essieu requis', severity: 'error' })
  } else {
    config.axles.forEach((axle, idx) => {
      if (!axle.id) {
        errors.push({ field: `axles[${idx}].id`, message: 'ID essieu requis', severity: 'error' })
      }
      if (axle.position === undefined || axle.position === null) {
        errors.push({ field: `axles[${idx}].position`, message: 'Position requise', severity: 'error' })
      }
      if (!axle.maxWeight || axle.maxWeight <= 0) {
        errors.push({ field: `axles[${idx}].maxWeight`, message: 'Poids max requis et > 0', severity: 'error' })
      }
      if (axle.emptyWeight && axle.emptyWeight > axle.maxWeight) {
        errors.push({
          field: `axles[${idx}].emptyWeight`,
          message: 'Poids vide ne peut pas dépasser poids max',
          severity: 'warning'
        })
      }
    })
  }

  // Vérifier IDs uniques
  const ids = config.axles?.map(a => a.id)
  const uniqueIds = new Set(ids)
  if (ids && ids.length !== uniqueIds.size) {
    errors.push({ field: 'axles', message: 'IDs d\'essieux doivent être uniques', severity: 'error' })
  }

  return errors
}

/**
 * Valide un véhicule complet
 * @param vehicle - Véhicule à valider
 * @returns Liste des erreurs/warnings
 */
export function validateVehicleEquipment(vehicle: Partial<Vehicle>): ValidationError[] {
  const errors: ValidationError[] = []

  // Valider la configuration d'essieux
  const axleConfig = typeof vehicle.axle_configuration === 'object'
    ? (vehicle.axle_configuration as VehicleAxleConfiguration)
    : null

  errors.push(...validateAxleConfiguration(axleConfig))

  // Valider les dimensions
  if (!vehicle.length || vehicle.length <= 0) {
    errors.push({ field: 'length', message: 'Longueur requise et > 0', severity: 'error' })
  }
  if (!vehicle.width || vehicle.width <= 0) {
    errors.push({ field: 'width', message: 'Largeur requise et > 0', severity: 'error' })
  }
  if (!vehicle.height || vehicle.height <= 0) {
    errors.push({ field: 'height', message: 'Hauteur requise et > 0', severity: 'error' })
  }

  // Valider la sellette si présente
  if (vehicle.fifth_wheel) {
    if (!vehicle.fifth_wheel.position || vehicle.fifth_wheel.position <= 0) {
      errors.push({ field: 'fifth_wheel.position', message: 'Position sellette requise', severity: 'error' })
    }
    if (!vehicle.fifth_wheel.maxLoad || vehicle.fifth_wheel.maxLoad <= 0) {
      errors.push({ field: 'fifth_wheel.maxLoad', message: 'Charge max sellette requise', severity: 'error' })
    }
  }

  return errors
}

/**
 * Crée une configuration d'essieux par défaut selon le type de véhicule
 * @param vehicleType - Type de véhicule
 * @returns Configuration par défaut
 */
export function createDefaultAxleConfiguration(vehicleType: string): VehicleAxleConfiguration {
  const defaults: Record<string, VehicleAxleConfiguration> = {
    VAN: {
      physicsType: 'van',
      trackWidth: 1800,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'front', position: 1000, type: 'steering', maxWeight: 1800, emptyWeight: 900 },
        { id: 'rear', position: 3200, type: 'drive', maxWeight: 1950, emptyWeight: 700 },
      ],
    },
    TRUCK: {
      physicsType: 'rigid_truck',
      trackWidth: 2500,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'front', position: 1200, type: 'steering', maxWeight: 7500, emptyWeight: 3200 },
        { id: 'rear', position: 5800, type: 'drive', maxWeight: 11500, emptyWeight: 2500 },
      ],
    },
    TRAILER: {
      physicsType: 'tractor_semitrailer',
      trackWidth: 2500,
      region: 'EU',
      enforceAxleLimits: true,
      kingPinPosition: 1500,
      fifthWheelMaxLoad: 12000,
      axles: [
        { id: 'trailer_1', position: 9200, type: 'trailer', maxWeight: 9500, emptyWeight: 2200 },
        { id: 'trailer_2', position: 10600, type: 'trailer', maxWeight: 9500, emptyWeight: 2200 },
      ],
    },
    CONTAINER: {
      physicsType: 'container',
      trackWidth: 2438,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'front', position: 800, type: 'other', maxWeight: 15240, emptyWeight: 1100 },
        { id: 'rear', position: 5300, type: 'other', maxWeight: 15240, emptyWeight: 1100 },
      ],
    },
  }

  return defaults[vehicleType] || defaults.TRUCK
}

/**
 * Déduit le preset correspondant à un véhicule basé sur type et vehicle_subtype
 * @param vehicle - Véhicule à analyser
 * @returns Le preset correspondant ou null si aucun match
 */
export function deducePresetFromVehicle(vehicle: Partial<Vehicle>): VehicleEquipmentPreset | null {
  if (!vehicle.type || !vehicle.vehicle_subtype) {
    return null
  }

  const allPresets = getAllPresets()

  // Cas spécial pour le porteur 2 essieux 12t : déduire la variante selon la position de l'essieu arrière
  if (vehicle.type === 'TRUCK' && vehicle.vehicle_subtype === 'porteur_2_essieux_12t') {
    const axleConfig = vehicle.axle_configuration as any
    const rearAxlePosition = axleConfig?.axles?.find((a: any) => a.type === 'drive')?.position

    if (rearAxlePosition) {
      if (rearAxlePosition <= 5200) return allPresets.find(p => p.id === 'rigid_truck_2_axles_short') || null
      if (rearAxlePosition <= 5550) return allPresets.find(p => p.id === 'rigid_truck_2_axles_standard') || null
      if (rearAxlePosition <= 5850) return allPresets.find(p => p.id === 'rigid_truck_2_axles_long') || null
      return allPresets.find(p => p.id === 'rigid_truck_2_axles_extra_long') || null
    }
  }

  // Cas spécial pour le porteur 2 essieux 18t : déduire la variante selon la position de l'essieu arrière
  if (vehicle.type === 'TRUCK' && vehicle.vehicle_subtype === 'porteur_2_essieux_18t') {
    const axleConfig = vehicle.axle_configuration as any
    const rearAxlePosition = axleConfig?.axles?.find((a: any) => a.type === 'drive')?.position

    if (rearAxlePosition) {
      if (rearAxlePosition <= 5650) return allPresets.find(p => p.id === 'rigid_truck_2_axles_18t_short') || null
      if (rearAxlePosition <= 6000) return allPresets.find(p => p.id === 'rigid_truck_2_axles_18t_standard') || null
      return allPresets.find(p => p.id === 'rigid_truck_2_axles_18t_long') || null
    }
  }

  // Cas spécial pour le porteur 3 essieux : déduire la variante selon la position de l'essieu drive
  if (vehicle.type === 'TRUCK' && vehicle.vehicle_subtype === 'porteur_3_essieux_26t') {
    const axleConfig = vehicle.axle_configuration as any
    const driveAxlePosition = axleConfig?.axles?.find((a: any) => a.type === 'drive')?.position

    if (driveAxlePosition) {
      if (driveAxlePosition <= 5400) return allPresets.find(p => p.id === 'rigid_truck_3_axles_standard') || null
      return allPresets.find(p => p.id === 'rigid_truck_3_axles_long') || null
    }
  }

  // Mapping inverse: vehicle_subtype -> preset_id
  const subtypeToPresetId: Record<string, string> = {
    'fourgonnette_standard': 'van_standard',
    'porteur_2_essieux_12t': 'rigid_truck_2_axles_standard',
    'porteur_2_essieux_18t': 'rigid_truck_2_axles_18t_standard',
    'porteur_3_essieux_26t': 'rigid_truck_3_axles_standard',
    'tracteur_4x2': 'tractor_4x2',
    'tracteur_6x2': 'tractor_6x2',
    'semi_remorque_2_essieux_34t': 'semi_trailer_2_axles',
    'semi_remorque_3_essieux_44t': 'semi_trailer_3_axles',
    'remorque_2_essieux': 'trailer_2_axles',
    'remorque_3_essieux': 'trailer_3_axles',
    // Conteneurs
    'dry_20': 'container_20ft',
    'dry_40': 'container_40ft',
    'dry_45': 'container_45ft_high_cube',
    'high_cube_40': 'container_40ft_high_cube',
    'high_cube_45': 'container_45ft_high_cube',
    'reefer_20': 'container_20ft',
    'reefer_40': 'container_40ft',
    'open_top_20': 'container_20ft',
    'open_top_40': 'container_40ft',
    'flat_rack_20': 'container_20ft',
    'flat_rack_40': 'container_40ft',
    'tank_20': 'container_20ft',
    'tank_40': 'container_40ft',
  }

  const presetId = subtypeToPresetId[vehicle.vehicle_subtype]
  if (!presetId) {
    return null
  }

  const preset = allPresets.find(p => p.id === presetId)

  // Vérifier que le type correspond aussi
  if (preset && preset.vehicleType === vehicle.type) {
    return preset
  }

  return null
}
